<script setup>
import { computed, onActivated, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import DropdownSelect from 'dashboard/components-next/dropdown-select/DropdownSelect.vue';
import MessageSignaturesAPI from 'dashboard/api/aceleres/messageSignatures';

const { t } = useI18n();
const route = useRoute();
const records = ref([]);
const manager = ref(false);
const loading = ref(true);
const failed = ref(false);
const saving = ref(new Set());
const search = ref('');
const status = ref('all');
const teamId = ref('all');
const accountId = computed(() => route.params.accountId);
const active = computed(() => route.name === 'signature_settings');
const seller = computed(() => records.value[0]);
const teams = computed(() => {
  const byId = new Map();
  records.value.forEach(user =>
    user.teams.forEach(team => byId.set(team.id, team))
  );
  return [...byId.values()];
});
const teamOptions = computed(() => [
  { value: 'all', label: t('SIGNATURE_SETTINGS.ALL_TEAMS') },
  ...teams.value.map(team => ({ value: String(team.id), label: team.name })),
]);
const matchingUsers = computed(() => {
  const term = search.value.trim().toLocaleLowerCase();
  return records.value.filter(
    user =>
      `${user.name} ${user.email}`.toLocaleLowerCase().includes(term) &&
      (teamId.value === 'all' ||
        user.teams.some(team => String(team.id) === teamId.value))
  );
});
const tabs = computed(() => [
  {
    value: 'active',
    label: t('SIGNATURE_SETTINGS.ACTIVE_TAB'),
    count: matchingUsers.value.filter(user => user.enabled).length,
  },
  {
    value: 'inactive',
    label: t('SIGNATURE_SETTINGS.INACTIVE_TAB'),
    count: matchingUsers.value.filter(user => !user.enabled).length,
  },
  {
    value: 'all',
    label: t('SIGNATURE_SETTINGS.ALL_TAB'),
    count: matchingUsers.value.length,
  },
]);
const visibleUsers = computed(() =>
  matchingUsers.value.filter(
    user =>
      status.value === 'all' || user.enabled === (status.value === 'active')
  )
);
const initials = name =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();
const fetchRecords = async () => {
  loading.value = true;
  failed.value = false;
  try {
    const { data } = await MessageSignaturesAPI.list(accountId.value);
    records.value = data.payload;
    manager.value = data.meta.manager;
  } catch {
    failed.value = true;
    useAlert(t('SIGNATURE_SETTINGS.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};
const toggle = async user => {
  saving.value.add(user.id);
  try {
    const { data } = await MessageSignaturesAPI.update(
      accountId.value,
      user.id,
      !user.enabled
    );
    records.value = records.value.map(record =>
      record.id === user.id ? data.payload : record
    );
    useAlert(t('SIGNATURE_SETTINGS.SAVED'));
  } catch {
    useAlert(t('SIGNATURE_SETTINGS.SAVE_ERROR'));
  } finally {
    saving.value.delete(user.id);
  }
};
onActivated(fetchRecords);
</script>

<template>
  <section
    class="signature-page w-full px-12 pb-8 font-inter text-[#171d26] max-[720px]:px-4"
  >
    <Teleport v-if="active" defer to=".settings-wrapper__teams-header-outlet">
      <header class="signature-page__header flex items-center gap-4 pb-6">
        <span
          class="signature-page__icon grid size-[52px] shrink-0 place-items-center rounded-2xl bg-white shadow-sm"
        >
          <Icon icon="i-lucide-signature" class="size-6" />
        </span>
        <div class="min-w-0">
          <h1
            class="signature-page__title m-0 text-[28px] font-semibold max-[720px]:text-[22px]"
          >
            {{ t('SIGNATURE_SETTINGS.TITLE') }}
          </h1>
          <p class="m-0 mt-1 text-sm text-[#647084]">
            {{
              manager
                ? t('SIGNATURE_SETTINGS.MANAGER_DESCRIPTION')
                : t('SIGNATURE_SETTINGS.SELLER_DESCRIPTION')
            }}
          </p>
        </div>
      </header>
    </Teleport>
    <p v-if="loading" class="py-12 text-center text-[#647084]" role="status">
      {{ t('SIGNATURE_SETTINGS.LOADING') }}
    </p>
    <div v-else-if="failed" class="rounded-2xl bg-white p-8 text-center">
      <p>{{ t('SIGNATURE_SETTINGS.LOAD_ERROR') }}</p>
      <button
        type="button"
        class="signature-page__retry rounded-full bg-[#171d26] px-5 py-2 text-white"
        @click="fetchRecords"
      >
        {{ t('SIGNATURE_SETTINGS.RETRY') }}
      </button>
    </div>
    <template v-else-if="manager">
      <label
        class="signature-page__search mb-7 flex h-12 w-full max-w-[394px] items-center gap-3 rounded-xl border border-[#ececef] bg-white px-4"
      >
        <Icon icon="i-lucide-search" class="size-4 text-[#647084]" />
        <input
          v-model="search"
          type="search"
          :aria-label="t('SIGNATURE_SETTINGS.SEARCH')"
          :placeholder="t('SIGNATURE_SETTINGS.SEARCH')"
          class="signature-page__search-input reset-base min-w-0 !min-h-0 !appearance-none !rounded-none ![outline:none] ![box-shadow:none] !m-0 !h-full !w-full !border-0 !bg-transparent !p-0 !text-sm !shadow-none focus:!outline-none"
        />
      </label>
      <div
        class="signature-page__card overflow-visible rounded-[24px] bg-white shadow-sm"
      >
        <div
          class="flex flex-wrap items-center justify-between gap-4 border-b border-[#f1f1f2] px-6 py-5 max-[720px]:px-4"
        >
          <div
            class="signature-page__tabs flex gap-1 rounded-full bg-[#f5f5f3] p-1 max-[720px]:w-full"
            :aria-label="t('SIGNATURE_SETTINGS.FILTER')"
          >
            <button
              v-for="tab in tabs"
              :key="tab.value"
              type="button"
              :aria-pressed="status === tab.value"
              class="signature-page__tab flex items-center justify-center gap-2 rounded-full px-3 py-2 text-sm font-semibold max-[720px]:min-w-0 max-[720px]:flex-1 max-[720px]:gap-1 max-[720px]:px-1 max-[720px]:text-xs"
              :class="
                status === tab.value
                  ? 'bg-[#171d26] text-white'
                  : 'bg-transparent text-[#4b5563]'
              "
              @click="status = tab.value"
            >
              {{ tab.label
              }}<span
                class="rounded-full px-1.5 text-xs"
                :class="status === tab.value ? 'bg-white/20' : 'bg-[#e8e9e9]'"
                >{{ tab.count }}</span
              >
            </button>
          </div>
          <div
            v-if="teams.length"
            class="signature-page__team-filter flex min-w-0 items-center gap-3 text-sm max-[720px]:w-full"
          >
            <span class="shrink-0 font-medium">
              {{ t('SIGNATURE_SETTINGS.TEAM') }}
            </span>
            <div class="w-[240px] min-w-0 max-w-full max-[720px]:flex-1">
              <DropdownSelect
                v-model="teamId"
                :options="teamOptions"
                :aria-label="t('SIGNATURE_SETTINGS.TEAM')"
                class="signature-page__team-select"
              />
            </div>
          </div>
        </div>
        <div class="hidden overflow-x-auto min-[721px]:block">
          <table
            class="signature-page__table w-full min-w-[640px] border-collapse text-left text-sm"
          >
            <thead>
              <tr
                class="border-b border-[#f1f1f2] text-xs uppercase tracking-wider text-[#778193]"
              >
                <th class="px-7 py-4 font-semibold">
                  {{ t('SIGNATURE_SETTINGS.USER') }}
                </th>
                <th class="px-4 py-4 font-semibold">
                  {{ t('SIGNATURE_SETTINGS.PROFILE') }}
                </th>
                <th class="px-4 py-4 font-semibold">
                  {{ t('SIGNATURE_SETTINGS.TEAM') }}
                </th>
                <th class="px-7 py-4 text-right font-semibold">
                  {{ t('SIGNATURE_SETTINGS.SEND_SIGNATURE') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="user in visibleUsers"
                :key="user.id"
                class="border-b border-[#f1f1f2]"
              >
                <td class="px-7 py-4">
                  <div class="flex items-center gap-3">
                    <span
                      class="grid size-10 shrink-0 place-items-center rounded-full bg-[#171d26] text-xs font-semibold text-white"
                      >{{ initials(user.name) }}</span
                    >
                    <div>
                      <div class="font-semibold">{{ user.name }}</div>
                      <div class="text-xs text-[#778193]">{{ user.email }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-4 py-4 text-[#4b5563]">
                  {{
                    user.role === 'manager'
                      ? t('SIGNATURE_SETTINGS.MANAGER')
                      : t('SIGNATURE_SETTINGS.SELLER')
                  }}
                </td>
                <td class="px-4 py-4 text-[#4b5563]">
                  {{
                    user.teams.map(team => team.name).join(', ') ||
                    t('SIGNATURE_SETTINGS.NO_TEAM')
                  }}
                </td>
                <td class="px-7 py-4">
                  <div class="flex items-center justify-end gap-3">
                    <span
                      class="rounded-lg px-2.5 py-1 text-xs font-semibold"
                      :class="
                        user.enabled
                          ? 'bg-[#e3f4ea] text-[#167640]'
                          : 'bg-[#eeeeec] text-[#4b5563]'
                      "
                      >{{
                        user.enabled
                          ? t('SIGNATURE_SETTINGS.ENABLED')
                          : t('SIGNATURE_SETTINGS.DISABLED')
                      }}</span
                    ><button
                      type="button"
                      role="switch"
                      :aria-checked="user.enabled"
                      :aria-label="
                        t('SIGNATURE_SETTINGS.TOGGLE_USER', { name: user.name })
                      "
                      :disabled="saving.has(user.id)"
                      class="signature-page__toggle relative h-[30px] w-[52px] shrink-0 rounded-full transition-colors disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                      :class="user.enabled ? 'bg-[#171d26]' : 'bg-[#b9bec6]'"
                      @click="toggle(user)"
                    >
                      <span
                        class="absolute top-1 size-[22px] rounded-full bg-white shadow-sm transition-all"
                        :class="user.enabled ? 'left-[26px]' : 'left-1'"
                      />
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="!visibleUsers.length">
                <td colspan="4" class="px-7 py-12 text-center text-[#647084]">
                  {{ t('SIGNATURE_SETTINGS.EMPTY') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="signature-page__mobile-list min-[721px]:hidden">
          <article
            v-for="user in visibleUsers"
            :key="user.id"
            class="signature-page__mobile-user min-w-0 border-b border-[#f1f1f2] p-4"
          >
            <div class="flex min-w-0 items-center gap-3">
              <div
                class="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#f1f2f4] text-sm font-semibold"
              >
                {{ initials(user.name) }}
              </div>
              <div class="min-w-0 flex-1">
                <div class="break-words text-sm font-semibold">
                  {{ user.name }}
                </div>
                <div class="break-all text-xs text-[#778193]">
                  {{ user.email }}
                </div>
              </div>
            </div>
            <div class="mt-3 space-y-1 text-xs text-[#4b5563]">
              <p class="m-0">
                <span class="font-semibold"
                  >{{ t('SIGNATURE_SETTINGS.PROFILE') }}:</span
                >
                {{
                  user.role === 'manager'
                    ? t('SIGNATURE_SETTINGS.MANAGER')
                    : t('SIGNATURE_SETTINGS.SELLER')
                }}
              </p>
              <p class="m-0 break-words">
                <span class="font-semibold"
                  >{{ t('SIGNATURE_SETTINGS.TEAM') }}:</span
                >
                {{
                  user.teams.map(team => team.name).join(', ') ||
                  t('SIGNATURE_SETTINGS.NO_TEAM')
                }}
              </p>
            </div>
            <div class="mt-4 flex items-center justify-between gap-3">
              <div class="min-w-0">
                <p class="m-0 text-xs font-medium">
                  {{ t('SIGNATURE_SETTINGS.SEND_SIGNATURE') }}
                </p>
                <span
                  class="text-xs"
                  :class="user.enabled ? 'text-[#167640]' : 'text-[#647084]'"
                >
                  {{
                    user.enabled
                      ? t('SIGNATURE_SETTINGS.ENABLED')
                      : t('SIGNATURE_SETTINGS.DISABLED')
                  }}
                </span>
              </div>
              <button
                type="button"
                role="switch"
                :aria-checked="user.enabled"
                :aria-label="
                  t('SIGNATURE_SETTINGS.TOGGLE_USER', { name: user.name })
                "
                :disabled="saving.has(user.id)"
                class="signature-page__toggle relative h-[30px] w-[52px] shrink-0 rounded-full transition-colors disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                :class="user.enabled ? 'bg-[#171d26]' : 'bg-[#b9bec6]'"
                @click="toggle(user)"
              >
                <span
                  class="absolute top-1 size-[22px] rounded-full bg-white shadow-sm transition-all"
                  :class="user.enabled ? 'left-[26px]' : 'left-1'"
                />
              </button>
            </div>
          </article>
          <p
            v-if="!visibleUsers.length"
            class="m-0 px-4 py-8 text-center text-sm text-[#647084]"
          >
            {{ t('SIGNATURE_SETTINGS.EMPTY') }}
          </p>
        </div>
        <p class="m-0 px-7 py-5 text-sm text-[#647084] max-[720px]:px-4">
          {{
            t('SIGNATURE_SETTINGS.COUNT', {
              shown: visibleUsers.length,
              total: records.length,
            })
          }}
        </p>
      </div>
    </template>
    <div
      v-else-if="seller"
      class="signature-page__card overflow-hidden rounded-[24px] bg-white shadow-sm"
    >
      <div
        class="flex flex-wrap items-center justify-between gap-4 border-b border-[#f1f1f2] px-7 py-6 max-[720px]:px-4"
      >
        <div>
          <h2 class="m-0 text-base font-semibold">
            {{ t('SIGNATURE_SETTINGS.SEND_TO_CLIENT') }}
          </h2>
          <p class="m-0 mt-1 text-sm text-[#647084]">
            {{ t('SIGNATURE_SETTINGS.EXPLANATION') }}
          </p>
        </div>
        <div class="flex items-center gap-3">
          <span
            class="rounded-lg px-3 py-1.5 text-xs font-semibold"
            :class="
              seller.enabled
                ? 'bg-[#e3f4ea] text-[#167640]'
                : 'bg-[#eeeeec] text-[#4b5563]'
            "
            >{{
              seller.enabled
                ? t('SIGNATURE_SETTINGS.ENABLED')
                : t('SIGNATURE_SETTINGS.DISABLED')
            }}</span
          ><button
            type="button"
            role="switch"
            :aria-checked="seller.enabled"
            :aria-label="t('SIGNATURE_SETTINGS.SEND_TO_CLIENT')"
            :disabled="saving.has(seller.id)"
            class="signature-page__toggle relative h-[30px] w-[52px] shrink-0 rounded-full transition-colors disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            :class="seller.enabled ? 'bg-[#171d26]' : 'bg-[#b9bec6]'"
            @click="toggle(seller)"
          >
            <span
              class="absolute top-1 size-[22px] rounded-full bg-white shadow-sm transition-all"
              :class="seller.enabled ? 'left-[26px]' : 'left-1'"
            />
          </button>
        </div>
      </div>
      <div class="grid gap-6 px-7 py-6 max-[720px]:px-4 md:grid-cols-2">
        <div
          v-for="side in ['internal', 'client']"
          :key="side"
          class="signature-page__preview"
        >
          <h3
            class="mb-2 text-xs font-semibold uppercase tracking-wider text-[#778193]"
          >
            {{
              side === 'internal'
                ? t('SIGNATURE_SETTINGS.INTERNAL_PREVIEW')
                : t('SIGNATURE_SETTINGS.CLIENT_PREVIEW')
            }}
          </h3>
          <div
            class="flex min-h-[120px] items-center justify-end rounded-2xl bg-[#eeeae2] p-5"
          >
            <div
              class="max-w-full rounded-xl bg-[#dff8d8] px-4 py-3 text-sm shadow-sm"
            >
              <strong
                v-if="side === 'internal' || seller.enabled"
                class="block"
                >{{
                  t('SIGNATURE_SETTINGS.SIGNED_NAME', {
                    name: seller.signature,
                  })
                }}</strong
              ><span>{{ t('SIGNATURE_SETTINGS.EXAMPLE') }}</span
              ><span class="mt-1 block text-right text-xs text-[#647084]">{{
                t('SIGNATURE_SETTINGS.PREVIEW_TIME')
              }}</span>
            </div>
          </div>
        </div>
      </div>
      <p class="m-0 border-t border-[#f1f1f2] px-7 py-5 text-sm text-[#647084]">
        {{ t('SIGNATURE_SETTINGS.INTERNAL_NOTICE') }}
      </p>
    </div>
  </section>
</template>
